import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { lotsByCompanyID } from "graphql/queries";
import { updateLot } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import LotCreateForm from "./LotCreateForm";
import LotUpdateForm from "./LotUpdateForm";
import LotPhotoDemarcation from "./LotPhotoDemarcation";

const Lots = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "agro:read",
    "agro:read",
    "agro:read",
  ]);
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotDetails, setLotDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    lotId: null,
    lotName: null,
  });
  const [modalLot, setModalLot] = useState({
    visible: false,
    lotId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      lots.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                lotDetails?.id === item.id
                  ? strings.lots.hide
                  : strings.lots.show
              }
              icon={lotDetails?.id === item.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                lotDetails?.id === item.id
                  ? setLotDetails(null)
                  : setLotDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.lots.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            <ActionIcon
              name={strings.lots.readPhotos}
              icon="Map"
              color="primary"
              onClick={() => openModal("photo", item.id)}
            />
            {canDelete && (
              <ActionIcon
                name={strings.lots.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    lotId: item.id,
                    lotName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lots, lotDetails?.id]
  );

  const onNext = () => {
    fetchLots(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchLots(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchLots(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalLot({ visible: true, action, lotId: id });
  const onSuccessLot = () => {
    setModalLot({ visible: false });
    fetchLots();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByLots = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setLots((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchLots(token, isNext = true, keywords) {
    const nameOperation = lotsByCompanyID;
    const nameOperationStr = "lotsByCompanyID";
    try {
      setLotsLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const lotsData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                    {
                      location: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const lotsItems = lotsData.data[nameOperationStr].items;
      setLots(lotsItems);
      getNamesByLots(lotsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: lotsData.data[nameOperationStr].nextToken,
        };
      });
      setLotsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setLotsLoading(false);
    } finally {
      setLotsLoading(false);
    }
  }
  async function lotDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateLot, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateLot.id;
      setLots(lots.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        lotId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchLots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "25%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "location",
      label: strings.table.headers.location,
      _style: { width: "25%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "25%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.lots.addLot}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {lotsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!lotDetails?.id} style={styles.collapseCard}>
          <CCard key={lotDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{lotDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b> {lotDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.location}:</b> {lotDetails?.location}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(lotDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.lotName,
        })}
        onCancel={() => setModalDetails({ visible: false, lotId: null })}
        onConfirm={() => lotDelete(modalDetails.lotId)}
      />
      {modalLot.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalLot.visible}
          onClose={() => setModalLot({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalLot.action === "create" && strings.modalLot.addTitle}
              {modalLot.action === "update" && strings.modalLot.updateTitle}
              {modalLot.action === "photo" && strings.modalLot.photoTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalLot.action === "create" && (
                <LotCreateForm onSuccess={onSuccessLot} />
              )}
              {modalLot.action === "update" && (
                <LotUpdateForm
                  lotId={modalLot.lotId}
                  onSuccess={onSuccessLot}
                />
              )}
              {modalLot.action === "photo" && (
                <LotPhotoDemarcation
                  lotId={modalLot.lotId}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Lotes",
    successDeleted: "Lote eliminado con éxito",
    lots: {
      addLot: "Agregar Lote",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
      readPhotos: "Ver Foto del Lote",
      docs: "Documentos",
    },
    responses: {
      errorFetch: "Error al consultar lote",
    },
    table: {
      headers: {
        name: "Nombre",
        location: "Ubicación",
        companyName: "Empresa",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Lote",
      description: "Descripción",
      location: "Ubicación",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el lote {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalLot: {
      addTitle: "Agregar Lote",
      updateTitle: "Actualizar Lote",
      photoTitle: "Foto del Lote",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Lots;
